import type {
  ApiResponse,
  MonthlyData,
  Project,
  Organization,
  YearlyData,
} from '../types/shared';
import { trackApiCall, trackError } from './rumService';

// Re-export for backward compatibility
export type ProjectData = Project;
export type { MonthlyData, ApiResponse, Organization };

class ApiService {
  private baseUrl =
    process.env.NODE_ENV === 'production'
      ? '/api'
      : 'http://localhost:3001/api';
  private token: string | null = null;

  /**
   * Create headers for API requests
   * Authentication is now handled via HTTP-only cookies automatically
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    return headers;
  }

  /**
   * Enhanced fetch wrapper with RUM tracking
   */
  private async fetchWithTracking(
    url: string,
    options: RequestInit
  ): Promise<Response> {
    const startTime = Date.now();
    const endpoint = url.replace(this.baseUrl, '');
    const method = options.method || 'GET';

    try {
      const response = await fetch(url, options);
      const duration = Date.now() - startTime;

      trackApiCall(endpoint, method, duration, response.status, response.ok);

      return response;
    } catch (error) {
      const duration = Date.now() - startTime;

      trackApiCall(endpoint, method, duration, 0, false);

      if (error instanceof Error) {
        trackError(error, `API call to ${endpoint}`, {
          method,
          duration,
          endpoint,
        });
      }

      throw error;
    }
  }

  async getCarbonImpactData(): Promise<{ monthly: MonthlyData[], annual: YearlyData[], projects: ProjectData[] }> {
    try {
      const response = await this.fetchWithTracking(
        `${this.baseUrl}/projects/carbon-impact`,
        {
          method: 'GET',
          headers: this.getHeaders(),
          credentials: 'include',
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(
            'Authentication failed. Please check your credentials.'
          );
        }
        if (response.status === 403) {
          throw new Error(
            'Access forbidden. You do not have permission to access this resource.'
          );
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<{ monthly: MonthlyData[], annual: YearlyData[], projects: ProjectData[] }> = await response.json();

      if (result?.success && result?.data) {
        // Return monthly, annual, and projects data
        return {
          monthly: Array.isArray(result.data.monthly) ? result.data.monthly : [],
          annual: Array.isArray(result.data.annual) ? result.data.annual : [],
          projects: Array.isArray(result.data.projects) ? result.data.projects : []
        };
      } else {
        throw new Error(
          result?.error?.message || 'API returned unsuccessful response'
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        // Handle specific error cases
        if (error.message.includes('Failed to fetch')) {
          throw new Error(
            'Unable to connect to the server. Please ensure the backend is running on localhost:3001'
          );
        }
        throw error;
      }
      throw new Error('An unknown error occurred');
    }
  }

  /**
   * Get current user's organization information
   */
  async getUserOrganization(): Promise<Organization> {
    try {
      const response = await this.fetchWithTracking(
        `${this.baseUrl}/user/organization`,
        {
          method: 'GET',
          headers: this.getHeaders(),
          credentials: 'include',
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(
            'Authentication failed. Please check your credentials.'
          );
        }
        if (response.status === 403) {
          throw new Error(
            'Access forbidden. You do not have permission to access this resource.'
          );
        }
        if (response.status === 404) {
          throw new Error('Organization not found.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: ApiResponse<Organization> = await response.json();

      if (result?.success && result?.data) {
        return result.data;
      } else {
        throw new Error(
          result?.error?.message || 'API returned unsuccessful response'
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        // Handle specific error cases
        if (error.message.includes('Failed to fetch')) {
          throw new Error(
            'Unable to connect to the server. Please ensure the backend is running on localhost:3001'
          );
        }
        throw error;
      }
      throw new Error('An unknown error occurred');
    }
  }

  /**
   * Send token to backend for secure cookie-based authentication
   * Simulates token reception from app.prodikt.com
   * @param token - The session token to send
   */
  async sendTokenToBackend(
    token: string
  ): Promise<ApiResponse<{ user: { id: string; organizationId?: string } }>> {
    try {
      const response = await this.fetchWithTracking(
        `${this.baseUrl}/auth/receive-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
          credentials: 'include', // Important for cookies
        }
      );

      const result: ApiResponse<{
        user: { id: string; organizationId?: string };
      }> = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.error?.message || `HTTP error! status: ${response.status}`
        );
      }

      return result;
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          throw new Error(
            'Unable to connect to the server. Please ensure the backend is running on localhost:3001'
          );
        }
        throw error;
      }
      throw new Error('An unknown error occurred');
    }
  }
}

export const apiService = new ApiService();
